import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckEditAction, CheckDeleteAction } from '../../../../Components/Utils/roles';
import { ViewButton, EditButton, DeleteButton } from '../../../../Components/Utils/ActionButtons ';
import ModalContainer from '../../../../Components/Others/ModalContainer';

// Customer Icon (building/company icon)
const CompanyIcon = ({ initial }) => (
  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-md text-white font-extrabold text-xl select-none uppercase">
    {initial || "C"}
  </div>
);

const CustomerCard = ({ item, fetcher, canEdit, canDelete, handleDelete, pageIndex, searchQuery }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleDeleteClick = async () => handleDelete("/customers/" + item?.customerId, setIsModalOpen);
  const initial = item?.companyName?.[0] || "C";
  const isActive = item?.status === "Active";
  const isInactive = item?.status === "In Active" || item?.status === "In active";

  const statusDot = isActive
    ? "bg-emerald-500"
    : isInactive
      ? "bg-rose-500"
      : "bg-gray-300";

  return (
    <div className="relative border border-y-gray-200/50 border-r-gray-200/50 border-l-0 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300 rounded-2xl bg-white p-5 flex flex-col overflow-hidden border-l-4 border-l-violet-500">

      {/* Top Header Row */}
      <div className="flex justify-between items-center gap-4 w-full">
        {/* Left: Icon + Company Name + ID */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Icon */}
          {item?.image?.file ? (
            <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-100 shrink-0">
              <img src={item.image.file} alt={item.companyName} className="w-full h-full object-cover" />
            </div>
          ) : (
            <CompanyIcon initial={initial} />
          )}

          {/* Name & ID */}
          <div className="flex flex-col min-w-0">
            <Link
              to={`/sales/customers/view/${item?.customerId}`}
              state={{
                data: item,
                pageIndex: pageIndex,
                searchQuery: searchQuery,
              }}
              className="text-gray-800 font-bold hover:underline text-[14px] leading-tight truncate"
            >
              {item?.companyName || "N/A"}
            </Link>
            <span className="text-[11px] text-gray-400 font-semibold mt-0.5">
              ID: {item?.customerId || "–"}
            </span>
            {item?.industry && (
              <span className="text-[10px] text-indigo-500 font-semibold mt-0.5 truncate">
                {item.industry}
              </span>
            )}
          </div>
        </div>

        {/* Right: Status badge */}
        <div className="shrink-0 flex items-center gap-1.5">
          <div className={`w-2.5 h-2.5 rounded-full ${statusDot}`} />
          <span className="text-[10px] font-bold text-gray-400 select-none">
            {item?.status || "–"}
          </span>
        </div>
      </div>

      {/* Middle Details Panel — horizontal layout like ShipmentTracking */}
      <div className="rounded-xl py-3 flex justify-between items-start gap-2 mt-3">
        {/* Email */}
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <span className="text-[10px] font-semibold text-gray-500 flex items-center gap-1 select-none">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email
          </span>
          <span className="text-[11px] font-bold text-gray-700 truncate leading-tight">
            {item?.email || "–"}
          </span>
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-100 shrink-0" />

        {/* Mobile */}
        <div className="flex flex-col gap-1 flex-1 pl-2 min-w-0">
          <span className="text-[10px] font-semibold text-gray-500 flex items-center gap-1 select-none">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Mobile
          </span>
          <span className="text-[11px] font-bold text-gray-700 truncate leading-tight">
            {item?.phoneWithCC || "–"}
          </span>
        </div>

        {/* Divider */}
        <div className="w-[1px] h-8 bg-gray-100 shrink-0" />

        {/* Location */}
        <div className="flex flex-col gap-1 flex-1 pl-2 min-w-0">
          <span className="text-[10px] font-semibold text-gray-500 flex items-center gap-1 select-none">
            <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Location
          </span>
          <span className="text-[11px] font-bold text-gray-700 truncate leading-tight">
            {item?.billing_city
              ? `${item.billing_city}${item?.billing_postal_code ? `, ${item.billing_postal_code}` : ""}`
              : item?.billing_postal_code || "–"}
          </span>
        </div>
      </div>

      {/* Contact Person row */}
      {item?.cpName && (
        <div className="flex items-center gap-1.5 mt-1">
          <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="text-[11px] text-gray-500 font-semibold">Contact: <span className="text-gray-700 font-bold">{item.cpName}</span></span>
        </div>
      )}

      {/* Sales Person row */}
      <div className="flex items-center gap-1.5 ">
        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="text-[11px] text-gray-500 font-semibold">Sales Person: <span className="text-gray-700 font-bold">{item?.assigned_sales_personName ? item?.assigned_sales_personName : "-"}</span></span>
      </div>


      {/* Action Buttons Row */}
      <div className="flex items-center justify-between gap-1.5 border-t border-gray-100 pt-3 mt-1 flex-wrap sm:flex-nowrap">
        <ViewButton
          viewLink={`/sales/customers/view/${item?.customerId}`}
          viewState={{
            data: item,
            pageIndex: pageIndex,
            searchQuery: searchQuery,
          }}
        />
        <div className="flex items-center gap-1.5">
          {CheckEditAction(item, "Customers", canEdit) && (
            <EditButton
              editLink={`/sales/customers/edit/${item?.customerId}`}
              editState={{
                data: item,
                from: "/sales/customers",
                pageIndex: pageIndex,
                searchQuery: searchQuery,
              }}
            />
          )}
          {CheckDeleteAction(item, canDelete) && handleDelete && (
            <DeleteButton
              // onDelete={() => handleDelete("/customers/", item?.customerId, item?.companyName, "Customer")}
              onDelete={() => setIsModalOpen(true)}
            />
          )}
        </div>
      </div>
      <ModalContainer
        visiable={isModalOpen}
        title="Warning"
        content={`Are you sure, ${item?.companyName} will be removed from Customers!`}
        handleClick={handleDeleteClick}
        closeModal={() => setIsModalOpen(false)}
      />
    </div>

  );
};

export default CustomerCard;